import {defineStore} from 'pinia'
import api from '@/services/api'
import {useAppStore} from '@/stores/appStore'
import {usePageContentDataStore} from '@/stores/pageContentData'
import {useTemplatesStore} from '@/stores/templates'
import {usePagesStore} from '@/stores/pages'
import {useItemDataStore} from '@/stores/itemData'

export function useSiteTreeStore(siteTreeId = 'siteTree') {
    return defineStore(siteTreeId, {
        state: () => ({
            lastSelectedPageId: false,
            lastSelectedContentId: false,
            selectedPageId: false,
            selectedContentId: false,
            selectedNavigationGroup: false,
            
            expandedPageIds: [],
            
            parentChildIds: {},
            
            groupedPageIds: {},
            
            navigationGroups: [],
            
            selectContentMode: false,
            showContent: false,
            showContentTypeButtons: false,
            
            onPageSelect: undefined,
            onContentSelect: undefined,
            
            propagatedContentId: undefined,
            propagationData: []
        }),
        
        actions: {
            addExpandedPageId(pageId) {
                if (this.expandedPageIds.indexOf(pageId) === -1) {
                    this.expandedPageIds.push(pageId)
                }
            },
            
            removeExpandedPageId(pageId) {
                const i = this.expandedPageIds.indexOf(pageId)
                if (i > -1) {
                    this.expandedPageIds.splice(i, 1)
                }
            },
            
            setSelectedNavigationGroup(group) {
                this.selectedNavigationGroup = group
            },
            
            setNavigationGroupsList(navigationGroups) {
                this.navigationGroups = navigationGroups
            },
            
            setSelectedPageId(selectedPageId) {
                this.selectedPageId = selectedPageId
            },
            
            setPropagationData({pageId, contentArea}) {
                const i = this.propagationData.findIndex(o => o.pageId === pageId)
                if (i > -1) {
                    this.propagationData.splice(i, 1)
                }
                
                this.propagationData.push({pageId, contentArea})
            },
            
            unsetPropagationData(pageId) {
                const i = this.propagationData.findIndex(o => o.pageId === pageId)
                if (i > -1) {
                    this.propagationData.splice(i, 1)
                }
            },
            
            async init(d) {
                const pageContentDataStore = usePageContentDataStore()
                const templatesStore = useTemplatesStore()
                const itemDataStore = useItemDataStore()
                const appStore = useAppStore()
                
                if (d) {
                    this.selectContentMode = d.selectContentMode || this.selectContentMode
                    this.showContent = d.showContent || this.selectContentMode || this.showContent
                    
                    this.onPageSelect = d.onPageSelect || undefined
                    this.onContentSelect = d.onContentSelect || undefined
                    
                    this.propagatedContentId = d.propagatedContentId || undefined
                    
                    if (d.propagatedContentId) {
                        this.propagationData = pageContentDataStore.items
                            .filter(o => o.contentId === d.propagatedContentId)
                            .map(o => ({pageId: o.pageId, contentArea: o.contentArea}))
                    }
                }
                
                const pagesStore = usePagesStore()
                await pagesStore.init()
                
                const response = await api.get('api/component/content')
                const objs = response.data.items
                
                itemDataStore.setItems({tableName: 'content', objs})
                
                await templatesStore.init(false)
                
                itemDataStore.setItems({
                    tableName: 'templates_templates',
                    objs: templatesStore.items
                })
                
                const navigation = (await itemDataStore.get({
                    tableName: 'pages',
                    id: appStore.settings?.homePage
                })).navigation
                
                setTimeout(() => {
                    if (!this.selectedNavigationGroup) {
                        this.selectedNavigationGroup = navigation
                    }
                })
                
                await this.setNavigationGroups()
                
                return this.setPageHierarchy()
            },
            
            async setNavigationGroups(reset) {
                if (this.navigationGroups.length === 0 || reset) {
                    const response = await api.get('api/component/pages/navigation', {
                        params: {sort: 'navigation', navigation: {ne: ''}}
                    })
                    this.setNavigationGroupsList(response.data.values)
                }
            },
            
            async setPageHierarchy() {
                const response = await api.get('api/component/pages', {
                    params: {
                        fields: ['id', 'navigation', 'parent', 'displayOrder'],
                        isArchived: 0,
                        sort: 'navigation,parent,displayOrder'
                    }
                })
                
                const objs = response.data.items
                const groupedPageIds = {}
                const parentChildIds = {}
                
                objs.forEach((obj) => {
                    if (obj.parent) {
                        if (parentChildIds[obj.parent] === undefined) {
                            parentChildIds[obj.parent] = []
                        }
                        parentChildIds[obj.parent].push(obj.id)
                    } else {
                        if (groupedPageIds[obj.navigation] === undefined) {
                            groupedPageIds[obj.navigation] = []
                        }
                        groupedPageIds[obj.navigation].push(obj.id)
                    }
                })
                
                this.parentChildIds = parentChildIds
                this.groupedPageIds = groupedPageIds
            },
            
            setPageId(pageId) {
                pageId = parseInt(pageId)
                
                if (this.selectedPageId) {
                    this.lastSelectedPageId = this.selectedPageId
                    this.lastSelectedContentId = false
                }
                this.selectedPageId = pageId
                this.selectedContentId = false
                
                if (pageId) {
                    this.setPagesNavigationGroup(pageId)
                }
            },
            
            setContentId(contentId) {
                const pageContentDataStore = usePageContentDataStore()
                const itemDataStore = useItemDataStore()
                contentId = parseInt(contentId)
                
                if (contentId === 0) {
                    if (this.selectedContentId) {
                        this.lastSelectedContentId = this.selectedContentId
                    } else if (this.selectedPageId) {
                        this.lastSelectedPageId = this.selectedPageId
                    }
                    this.selectedContentId = contentId
                    this.selectedPageId = false
                } else {
                    const obj = pageContentDataStore.items.find(o => o.id === contentId)
                    if (obj) {
                        const pageId = obj.pageId
                        return itemDataStore.get({
                                tableName: 'content',
                                id: obj.contentId
                            })
                            .then((contentData) => {
                                if (contentData) {
                                    if (this.selectedContentId) {
                                        this.lastSelectedContentId = this.selectedContentId
                                    } else if (this.selectedPageId) {
                                        this.lastSelectedPageId = this.selectedPageId
                                    }
                                    this.selectedContentId = contentId
                                    this.selectedPageId = false
                                    
                                    this.setPagesNavigationGroup(pageId)
                                }
                            })
                    }
                }
            },
            
            async setPagesNavigationGroup(pageId) {
                const itemDataStore = useItemDataStore()
                const {navigation} = await itemDataStore.get({
                    tableName: 'pages', id: pageId
                })
                
                if (navigation) {
                    this.selectedNavigationGroup = navigation
                }
            },
            
            setExpandedPageIds(pageId) {
                const itemDataStore = useItemDataStore()
                if (pageId) {
                    itemDataStore.get({
                            tableName: 'pages',
                            id: pageId
                        })
                        .then((obj) => {
                            if (obj.parent) {
                                this.addExpandedPageId(obj.parent)
                                this.setExpandedPageIds(obj.parent)
                            }
                        })
                }
            },
            
            refreshSiteTree() {
                const pageContentDataStore = usePageContentDataStore()
                return pageContentDataStore.init()
                    .then(() => {
                        return this.setNavigationGroups()
                    })
                    .then(() => {
                        return this.setPageHierarchy()
                    })
                    .then(() => {
                        if (this.selectedPageId) {
                            this.setPagesNavigationGroup(this.selectedPageId)
                        }
                    })
            }
        }
    })()
}